import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useCompare } from '@/lib/compareStore';

/**
 * Compare checkbox for listing cards.
 *
 * The card itself is a <Link>, so every interaction here stops propagation
 * and prevents the default navigation. Limits are explained, never silent:
 * a guest hitting their ceiling is told to sign in, and a type mismatch says
 * which ad type the shortlist already holds.
 */
export const CompareCheckbox = ({ listing, className = '' }) => {
  const { has, add, remove, maxColumns, isLoggedIn, userMax, listingType } = useCompare();
  const checked = has(listing.id);

  const onToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (checked) {
      remove(listing.id);
      return;
    }
    const res = add(listing);
    if (res.ok) return;
    if (res.reason === 'type_mismatch') {
      toast.error(`Your shortlist holds ${res.existingType} listings. Only the same ad type can be compared side by side.`);
      return;
    }
    if (!isLoggedIn) {
      toast.error(`Sign in to compare up to ${userMax}`, {
        description: `Guests can compare ${maxColumns} at a time. Your picks are kept when you sign in.`,
        action: { label: 'Sign in', onClick: () => { window.location.href = '/login'; } },
      });
      return;
    }
    toast.error(`You can compare up to ${maxColumns} listings at once. Remove one to add another.`);
  };

  return (
    <button
      type='button'
      onClick={onToggle}
      aria-pressed={checked}
      title={checked ? 'Remove from comparison' : 'Add to comparison'}
      data-testid={`compare-checkbox-${listing.id}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] font-semibold backdrop-blur transition-colors ${
        checked
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-background/90 text-foreground border-border hover:border-primary hover:text-primary'
      } ${className}`}
    >
      <Scale className='h-3 w-3' />
      {checked ? 'In compare' : 'Compare'}
    </button>
  );
};

export default CompareCheckbox;

/** Full-width action used on the listing detail page. */
export const CompareDetailButton = ({ listing }) => {
  const { has, add, remove, maxColumns, isLoggedIn, userMax } = useCompare();
  const checked = has(listing.id);
  const onClick = () => {
    if (checked) return remove(listing.id);
    const res = add(listing);
    if (res.ok) return;
    if (res.reason === 'type_mismatch') {
      toast.error(`Your shortlist holds ${res.existingType} listings. Only the same ad type can be compared side by side.`);
    } else if (!isLoggedIn) {
      toast.error(`Sign in to compare up to ${userMax}`, { description: `Guests can compare ${maxColumns} at a time.` });
    } else {
      toast.error(`You can compare up to ${maxColumns} listings at once.`);
    }
  };
  return (
    <button
      type='button'
      onClick={onClick}
      data-testid='listing-detail-compare-button'
      className={`inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors ${
        checked ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:border-primary hover:text-primary'
      }`}
    >
      <Scale className='h-4 w-4' />
      {checked ? 'In comparison' : 'Add to compare'}
    </button>
  );
};

/** Inline "what you get by signing in" nudge, used on the compare page. */
export const CompareGuestNudge = ({ guestMax, userMax }) => (
  <div
    className='flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'
    data-testid='compare-guest-nudge'
  >
    <Lock className='h-4 w-4 shrink-0' />
    <span>You&apos;re comparing {guestMax} at a time as a guest.</span>
    <Link to='/login' className='font-semibold underline underline-offset-2' data-testid='compare-guest-nudge-login'>
      Sign in to compare up to {userMax}
    </Link>
  </div>
);
